import { useEffect, useRef, useState } from "react";

/**
 * Renders any combination of the following badges next to a username:
 *   • Admin        → gold seal (check inside a starburst)
 *   • Verified user → blue seal
 *   • Silver plan  → silver cup
 *   • Gold plan    → gold cup
 *   • Platinum plan → diamond (platinum) cup
 *
 * Multiple badges can appear together (e.g. an admin who's also on the Gold
 * plan). Each badge has its own popover explaining what it means.
 */
export default function UserBadge({ user, size = 14, className = "" }) {
  if (!user) return null;
  const isAdmin = user.role === "admin";
  const isVerified = !!user.verified;
  const tier = TIERS[(user.membership || "").toLowerCase()];

  if (!isAdmin && !isVerified && !tier) return null;

  return (
    <span className={`inline-flex items-center gap-0.5 align-middle ${className}`}>
      {(isAdmin || isVerified) && (
        <SealBadge
          size={size}
          fill={isAdmin ? "#F59E0B" : "#1DA1F2"}
          testid={isAdmin ? "admin-badge" : "verified-badge"}
          title={isAdmin ? "Site Administrator" : "Verified account"}
          body={isAdmin
            ? "Official Start-A-TE staff. Manages the platform, reviews websites and payments, and can help with account questions."
            : "This user's identity has been reviewed and confirmed by the Start-A-TE admin team."}
        />
      )}
      {tier && (
        <TierCup
          size={size}
          tier={tier}
          testid={`tier-badge-${tier.key}`}
        />
      )}
    </span>
  );
}

// ---------- Tier cup ----------

// Colors + metadata for each paid tier. The keys match membership plan names
// (case-insensitive) so /admin/plans can grow without changes here.
const TIERS = {
  silver: {
    key: "silver",
    label: "Silver member",
    color: "#94A3B8",
    accent: "#E2E8F0",
    tagline: "Upgraded from Free — surfs harder, earns faster.",
    perks: [
      "500 daily surfs (5× Free)",
      "15% referral commission",
      "Silver cup badge site-wide",
    ],
  },
  gold: {
    key: "gold",
    label: "Gold member",
    color: "#F59E0B",
    accent: "#FEF3C7",
    tagline: "Priority queue and richer bonuses across the platform.",
    perks: [
      "1,500 daily surfs",
      "20% referral commission",
      "8-second surf timer",
      "Featured on Discover",
    ],
  },
  platinum: {
    key: "platinum",
    label: "Platinum member",
    color: "#38BDF8",
    accent: "#E0F2FE",
    tagline: "Top tier — maximum reach and revenue share.",
    perks: [
      "5,000 daily surfs",
      "25% referral commission",
      "Featured on Discover",
      "Priority support",
    ],
  },
};

function TierCup({ size, tier, testid }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex items-center align-middle">
      <button
        type="button"
        data-testid={testid}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={tier.label}
        className="inline-flex items-center align-middle cursor-pointer hover:scale-110 transition-transform duration-150"
        style={{ padding: 0, background: "transparent", border: "none" }}
      >
        <CupSvg size={size} color={tier.color} accent={tier.accent} />
      </button>
      {open && (
        <div
          data-testid={`${testid}-popover`}
          onClick={(e) => e.stopPropagation()}
          className="absolute z-40 left-1/2 -translate-x-1/2 top-full mt-2 w-72 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-4 text-left cursor-auto normal-case tracking-normal"
        >
          <div className="flex items-center gap-2 mb-2">
            <CupSvg size={20} color={tier.color} accent={tier.accent} />
            <span
              className="font-black text-sm leading-tight"
              style={{ color: tier.color }}
            >
              {tier.label}
            </span>
          </div>
          {tier.tagline && (
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed m-0 mb-2">
              {tier.tagline}
            </p>
          )}
          {Array.isArray(tier.perks) && tier.perks.length > 0 && (
            <ul className="mt-1 space-y-1 text-sm text-slate-700 dark:text-slate-200">
              {tier.perks.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ background: tier.color }}
                  />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

function CupSvg({ size, color, accent }) {
  // Simple trophy/cup silhouette so it reads well at small sizes.
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" className="inline-block shrink-0">
      {/* handles */}
      <path fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" d="M7 6C4 6 3 8 3 10c0 2 2 3 4 3" />
      <path fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" d="M17 6c3 0 4 2 4 4 0 2-2 3-4 3" />
      {/* cup body */}
      <path
        fill={color}
        d="M7 3h10v6a5 5 0 0 1-10 0V3z"
      />
      {/* accent highlight */}
      <path fill={accent} d="M8.4 4.2h1.6v3.6H8.4z" opacity="0.8" />
      {/* stem + base */}
      <path fill={color} d="M11 14h2v3h-2z" />
      <path fill={color} d="M8 19h8v2H8z" />
    </svg>
  );
}

// ---------- Admin / Verified seal ----------

function SealBadge({ size, fill, testid, title, body }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex items-center align-middle">
      <button
        type="button"
        data-testid={testid}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={title}
        className="inline-flex items-center align-middle cursor-pointer hover:scale-110 transition-transform duration-150"
        style={{ padding: 0, background: "transparent", border: "none" }}
      >
        <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" className="inline-block shrink-0">
          <path fill={fill} d="M12 2l2.39 2.03 3.13.06 1.14 2.9 2.7 1.62-.5 3.09.5 3.09-2.7 1.62-1.14 2.9-3.13.06L12 22l-2.39-2.03-3.13-.06-1.14-2.9-2.7-1.62.5-3.09-.5-3.09 2.7-1.62 1.14-2.9 3.13-.06L12 2z" />
          <path fill="#fff" d="M10.6 15.3l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z" />
        </svg>
      </button>
      {open && (
        <div
          data-testid={`${testid}-popover`}
          onClick={(e) => e.stopPropagation()}
          className="absolute z-40 left-1/2 -translate-x-1/2 top-full mt-2 w-64 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-4 text-left cursor-auto normal-case tracking-normal"
        >
          <div className="flex items-center gap-2 mb-2">
            <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true" className="shrink-0">
              <path fill={fill} d="M12 2l2.39 2.03 3.13.06 1.14 2.9 2.7 1.62-.5 3.09.5 3.09-2.7 1.62-1.14 2.9-3.13.06L12 22l-2.39-2.03-3.13-.06-1.14-2.9-2.7-1.62.5-3.09-.5-3.09 2.7-1.62 1.14-2.9 3.13-.06L12 2z" />
              <path fill="#fff" d="M10.6 15.3l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z" />
            </svg>
            <span className="font-bold text-slate-900 dark:text-white text-sm leading-tight">{title}</span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed m-0">{body}</p>
        </div>
      )}
    </span>
  );
}
